import { ReactComponent as plusIcon } from '@epam/assets/icons/common/action-add-12.svg';
import { Button, FlexCell, FlexRow, TabButton } from '@epam/promo';
import { useMemo, useRef } from 'react';
import { Outlet, useNavigate } from 'react-router-dom';

import { CardList } from 'components/broadcasts/card-list';
import { BlankBlock } from 'components/common';
import CardFilters from 'components/common/CardFilters/CardFilters';
import { useBroadcastSwitcher } from 'hooks';
import { useAppLocation } from 'hooks/helpers';
import { Broadcast } from 'models/broadcast.model';
import { broadcastRoutes } from 'router/broadcastRoutes';
import { TAB_STATUSES, resetDraft, useBroadcastStore } from 'store/broadcast';
import { useFavoritesStore } from 'store/favorites';
import css from './BroadcastPage.module.scss';

export const BroadcastPageView = () => {
  const navigate = useNavigate();
  const switchBroadcast = useBroadcastSwitcher();
  const {
    draft,
    inProgressBroadcasts,
    publishedBroadcasts,
    activeTab,
    actions: { setActiveTab },
  } = useBroadcastStore((state) => state);
  const { isCreate, isNotFound } = useAppLocation(broadcastRoutes);
  const panelRef = useRef<HTMLDivElement>(null);
  const favoriteBroadcastIds = useFavoritesStore(
    (state) => state.favoriteBroadcastIds,
  );

  const activeBroadcasts = useMemo(
    () =>
      activeTab === TAB_STATUSES.IN_PROGRESS
        ? inProgressBroadcasts
        : publishedBroadcasts,
    [activeTab, inProgressBroadcasts, publishedBroadcasts],
  );
  const items = isCreate ? [draft, ...activeBroadcasts] : [...activeBroadcasts];
  const lastInProgressBroadcastId = useRef<string>(null);
  const lastPublishedBroadcastId = useRef<string>(null);

  const showBroadcastData = () => {
    if (isNotFound) {
      return (
        <BlankBlock
          header="Broadcast is not found!"
          message="Please create another broadcast"
        />
      );
    }

    if (activeBroadcasts?.length || isCreate) {
      return <Outlet />;
    }

    return (
      <BlankBlock
        header="Nothing to display yet"
        message="Please create your first broadcast"
      />
    );
  };

  const navigateToTheFirstBroadcast = (
    list: Broadcast[],
    lastSavedId: string | null,
  ) => {
    if (!list.length) {
      return navigate(broadcastRoutes.notFound);
    }

    const newDraftbroadcast = lastSavedId
      ? list.find((broadcast) => broadcast.id === lastSavedId)
      : list[0];

    const route = newDraftbroadcast
      ? broadcastRoutes.broadcastId(newDraftbroadcast.id)
      : broadcastRoutes.notFound;

    return navigate(route);
  };

  const onInProgressTab = () => {
    if (activeTab === TAB_STATUSES.IN_PROGRESS) return;

    setActiveTab(TAB_STATUSES.IN_PROGRESS);
    lastPublishedBroadcastId.current = draft.id;

    return navigateToTheFirstBroadcast(
      inProgressBroadcasts,
      lastInProgressBroadcastId.current,
    );
  };

  const handleSwitchToPublishedTab = () => {
    resetDraft();
    setActiveTab(TAB_STATUSES.PUBLISHED);
    lastInProgressBroadcastId.current = draft.id;

    return navigateToTheFirstBroadcast(
      publishedBroadcasts,
      lastPublishedBroadcastId.current,
    );
  };

  const onPublishedTab = () => {
    if (activeTab === TAB_STATUSES.PUBLISHED) return;

    switchBroadcast({
      onSuccess: handleSwitchToPublishedTab,
      onAbort: handleSwitchToPublishedTab,
    });
  };

  return (
    <FlexRow cx={css.page__container}>
      <FlexCell ref={panelRef} cx={css['page__left-panel']} grow={1}>
        <Button
          caption="Add a new broadcast"
          color="blue"
          cx={css.page__btn}
          icon={plusIcon}
          rawProps={{ 'data-testid': 'add-broadcast-btn' }}
          onClick={() => {
            switchBroadcast({
              onSuccess: () => {
                navigate(broadcastRoutes.create);
                resetDraft();
                setActiveTab(TAB_STATUSES.IN_PROGRESS);
              },
              onAbort: () => {
                navigate(broadcastRoutes.create);
                resetDraft();
                setActiveTab(TAB_STATUSES.IN_PROGRESS);
              },
            });
          }}
        />
        <FlexCell>
          <FlexRow borderBottom>
            <TabButton
              caption={TAB_STATUSES.IN_PROGRESS}
              cx={css['page__tab-btn']}
              isActive={activeTab === TAB_STATUSES.IN_PROGRESS}
              size="36"
              onClick={onInProgressTab}
            />
            <TabButton
              caption={TAB_STATUSES.PUBLISHED}
              cx={css['page__tab-btn']}
              isActive={activeTab === TAB_STATUSES.PUBLISHED}
              size="36"
              onClick={onPublishedTab}
            />
          </FlexRow>
        </FlexCell>
        {activeBroadcasts?.length && (
          <CardFilters
            key={activeTab}
            CardList={CardList}
            favoritesFilter={{
              isFavorite: (item) => !!favoriteBroadcastIds[item.id],
            }}
            isFilterVisible={activeTab !== TAB_STATUSES.PUBLISHED}
            items={items}
            panelHeight={
              panelRef.current ? panelRef.current.clientHeight - 60 : 0
            }>
            <CardList items={items} />
          </CardFilters>
        )}
      </FlexCell>
      <FlexCell grow={3.5}>{showBroadcastData()}</FlexCell>
    </FlexRow>
  );
};
